import React from 'react';
import { Link } from 'react-router-dom';
import './TermsAndConditions.css';

function TermsAndConditions() {
  return (
    <div className="terms-container">
      <h1 className="text-center">Terms &amp; Conditions — Affordable Landscape (CSLB #940083)</h1>
      <p className="text-center"><em>Effective Date: October 23, 2025</em></p>

      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          By accessing or using the websites, appointment pages, staff / contractor portals, or services of Affordable Landscape
          (collectively, “Affordable Landscape,” “we,” “our,” or “us”), you agree to be bound by these Terms and Conditions
          (“Terms”). If you do not agree, please discontinue use of the sites and services immediately. These Terms apply to all
          visitors, homeowners, contractors, vendors, and authorized users of our systems.
        </p>
      </section>

      <section>
        <h2>2. Nature of Services &amp; No Professional Advice</h2>
        <p>
          Affordable Landscape provides construction and landscape design services, which may include turf sales (new, recycled, and
          liquidation rolls), landscape design and installation, grading, irrigation and drainage work, project management, and related
          consulting. Content on this site is for general informational and marketing purposes only and must not be interpreted as
          professional engineering, legal, tax, or financial advice. Project designs, concepts, and estimates are always subject to
          on‑site verification, written contracts, and applicable building codes. For urgent issues relating to an existing job or
          appointment, please call or text our main line at <strong>+1 (619) 587‑7052</strong>.
        </p>
      </section>

      <section>
        <h2>3. Appointments, Electronic Communications &amp; SMS Consent</h2>
        <p>
          By submitting a form, booking through our appointment pages, or communicating with us electronically, you authorize
          Affordable Landscape to collect, store, and use your information for scheduling appointments, preparing estimates and bids,
          managing active projects, and providing customer service. You agree that we may contact you by phone, SMS text message,
          email, or other electronic means for administrative, scheduling, and marketing purposes related to our services. Message
          frequency may vary; message and data rates may apply. You may opt out of marketing SMS at any time by replying STOP, or by
          contacting our office. Opting out of certain communications may limit our ability to provide services.
        </p>
      </section>

      <section>
        <h2>4. Privacy &amp; Data Protection</h2>
        <p>
          We collect personal information (such as names, contact details, property addresses, photos, videos, diagrams, and project
          documents) solely for the purpose of delivering our services, managing appointments and projects, improving operations, and
          complying with legal obligations. We do not sell your personal data to third parties for their independent marketing
          purposes. Limited sharing may occur with service providers (such as subcontractors, delivery services, hosting providers, or
          software platforms like Odoo) as necessary to perform work or operate our systems. For more details, please review our&nbsp;
          <Link to="/privacy">Privacy Policy</Link>.
        </p>
      </section>

      <section>
        <h2>5. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, Affordable Landscape, its owners, officers, employees, contractors, and affiliates
          shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits,
          loss of data, business interruption, or loss of use, arising from or related to your use of the site, portals, or services.
          Except where prohibited by law, and excluding cases of gross negligence or willful misconduct, our total liability for any
          claim shall not exceed the total amount you actually paid to Affordable Landscape for the specific service or project giving
          rise to the claim.
        </p>
      </section>

      <section>
        <h2>6. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Affordable Landscape, its officers, employees, agents, and affiliates from
          and against any claims, liabilities, damages, losses, and expenses (including reasonable attorney fees) arising out of or in
          any way connected with: (a) your breach of these Terms; (b) your misuse of our websites, portals, or services; (c) your
          violation of applicable laws; or (d) any materials or information you submit, upload, or transmit through our systems.
        </p>
      </section>

      <section>
        <h2>7. Third‑Party Platforms, Subcontractors &amp; Delivery Services</h2>
        <p>
          We may rely on third‑party platforms and vendors to deliver certain services, such as Odoo for appointments and project
          management, telecom providers for calls and SMS, payment processors, turf suppliers, delivery carriers, or independent
          subcontractors. While we exercise reasonable care in selecting these providers, Affordable Landscape is not responsible for acts,
          omissions, delays, or failures outside its reasonable control. You acknowledge that some work may be performed by licensed
          subcontractors under our direction, and by using our services you consent to this practice.
        </p>
      </section>

      <section>
        <h2>8. Force Majeure</h2>
        <p>
          Affordable Landscape will not be liable for any delay or failure to perform its obligations due to events beyond its reasonable control,
          including but not limited to natural disasters, extreme weather, fire, flood, labor disputes, acts of government, pandemics,
          supply chain disruptions, power failures, or telecommunications and internet outages. In such cases, timelines may be
          adjusted, and we will make commercially reasonable efforts to resume performance as soon as practicable.
        </p>
      </section>

      <section>
        <h2>9. Dispute Resolution &amp; Governing Law</h2>
        <p>
          These Terms shall be governed by and interpreted under the laws of the State of California and applicable federal law of the
          United States, without regard to conflict‑of‑law principles. In the event of any dispute, you agree to first attempt to
          resolve the matter in good‑faith discussions with us within thirty (30) days. If the dispute cannot be resolved informally,
          it shall be submitted to a court of competent jurisdiction located in San Diego County, California. You agree to the
          personal jurisdiction of such courts.
        </p>
      </section>

      <section>
        <h2>10. California Contracting Laws, Change Orders & Home Improvement Salespersons (HIS)</h2>
        <p>
          All construction and landscape work performed by Affordable Landscape complies with the California Contractors State License
          Board (CSLB) regulations, including rules governing licensed contractors, Home Improvement Salespersons (HIS), contract
          requirements, deposits, change orders, and cancellation rights.
        </p>

        <h3>10.1 Home Improvement Salesperson (HIS) Regulations</h3>
        <p>
          HIS representatives are authorized to present, negotiate, and facilitate Home Improvement Contracts on behalf of a
          Licensed Contractor. Under California law, an HIS may only operate under the license and direction of a Licensed
          Contractor and may not independently contract for work. HIS personnel may discuss and prepare estimates exceeding
          $500, as long as the final contract is issued and executed in the name of the licensed contractor.
        </p>

        <h3>10.2 Contract Requirements & Deposit Limits</h3>
        <p>
          California law prohibits contractors from requesting or accepting a down payment greater than <strong>10% of the contract
          price or $1,000, whichever is less</strong> for residential home improvement projects. Additional payments must follow
          a mutually agreed‑upon schedule and must comply with CSLB regulations.
        </p>

        <h3>10.3 Change Orders</h3>
        <p>
          Any modification, addition, or alteration to the original contract—including material changes, additional labor,
          upgraded turf selections, or scope expansions—must be documented in a <strong>written Change Order</strong> signed by both
          the homeowner (or authorized agent) and Affordable Landscape before the work is performed. Verbal change orders are not permitted.
          All change orders are considered part of the binding contract and are subject to the same payment, scheduling, and
          cancellation rules as the primary agreement.
        </p>
        <p>
          All Change Orders must include an official <strong>Diagram Number</strong> assigned exclusively by a Licensed Contractor, Licensed
          Subcontractor, Registered Home Improvement Salesperson (HIS), or an authorized Executive or
          Owner of Graystone Enterprises Inc. No Change Order may be drafted, submitted, or executed without a valid Diagram Number.
          Every Change Order must be reviewed and <strong>approved in advance</strong> by the Responsible Managing Officer (RMO) or license holder
          for Affordable Landscape. Change Orders are not valid, enforceable, or eligible for scheduling until they are <strong>signed by
          all parties</strong> with a contractual interest in the project, including the homeowner, the Licensed Contractor, and any
          participating HIS representative.
        </p>

        <h3>10.4 Right of Rescission (Cancellation Period)</h3>
        <p>
          California residents have the legal right to cancel a Home Improvement Contract within:
        </p>
        <ul>
          <li><strong>3 business days</strong> from the date of signing for most homeowners,</li>
          <li><strong>5 business days</strong> if the homeowner is <strong>65 years of age or older</strong>.</li>
        </ul>
        <p>
          This right applies when the contract is signed at the customer’s residence or any location other than the contractor’s
          place of business.
        </p>

        <h3>10.5 In‑Office Signings (Exemption)</h3>
        <p>
          If a homeowner or contractor signs the Home Improvement Contract at our office located at 519 N 41st St, San Diego, CA 92102,
          the statutory right of rescission does <strong>not</strong> apply. Work may begin immediately upon signing unless otherwise stated
          in writing.
        </p>

        <h3>10.6 Work Over $500</h3>
        <p>
          Under California law, any project with a value of <strong>$500 or more</strong> in labor and materials must be performed under the
          license of a CSLB‑licensed contractor. HIS representatives may facilitate the job, but all contracting responsibility
          and liability remain with the Licensed Contractor (Affordable Landscape under Graystone Enterprises Inc., where applicable).
        </p>
      </section>

      <section>
        <h2>10.7 Turf Loading, Vehicle Safety, Weight Disclosures & Zero‑Return Policy</h2>
        <p>
          By purchasing turf, scheduling a pickup, or accepting curbside delivery from Affordable Landscape or its affiliated turf yards,
          you acknowledge and agree to the following safety, liability, and product‑condition terms:
        </p>

        <h3>10.7.1 Heavy Material Disclosure</h3>
        <p>
          Recycled turf may contain <strong>sand and/or rubber infill weighing up to approximately 2,000 lbs per roll</strong>. These materials
          are remnants from prior installations and are considered part of the recycled product. Weight varies by roll and is not
          guaranteed. Customers are responsible for ensuring that their vehicle, trailer, or equipment is rated to safely carry the
          selected materials.
        </p>

        <h3>10.7.2 Vehicle Capacity & Equipment Responsibility</h3>
        <p>
          Customers are solely responsible for verifying the weight limits, towing capacity, suspension capability, and general safety
          condition of any vehicle or trailer used for pickup. Affordable Landscape, its employees, contractors, and subcontractors do not verify
          vehicle capacity and are not responsible for any damage, mechanical failure, bending, cracking, suspension collapse, blown
          tires, or any structural or mechanical issues that result from loading heavy turf rolls.
        </p>

        <h3>10.7.3 Loading Assistance Disclaimer</h3>
        <p>
          Loading assistance is offered as a courtesy only. By permitting Affordable Landscape personnel, contractors, or subcontractors to load
          turf into or onto your vehicle or trailer, you assume all risk associated with loading, shifting loads, equipment use, or
          accidental damage to your vehicle, trailer, accessories, tailgate, or personal property. <strong>Affordable Landscape assumes no liability for
          any damage, dents, scratches, broken components, cracked windshields, bent trailer frames, or injuries</strong> resulting from the
          loading process.
        </p>

        <h3>10.7.4 Tailgate Removal Requirement</h3>
        <p>
          Prior to unloading turf rolls at your destination, you must <strong>remove the tailgate</strong> from your truck or ensure it is fully
          lowered and secured out of the roll’s path. Failure to remove or secure the tailgate can result in serious tailgate damage,
          injuries, tipping hazards, or structural failure. Customers accept full responsibility for any damage or injury associated
          with tailgate removal, failure to remove the tailgate, or improper unloading procedures.
        </p>

        <h3>10.7.5 Zero‑Return, Final‑Sale Policy</h3>
        <p>
          All recycled turf is <strong>sold as‑is, final sale, with no returns, refunds, or exchanges</strong>. Recycled turf may include seams,
          markings, paint, sand, rubber infill, wear from prior athletic or commercial use, or other imperfections consistent with
          recycled materials. Once turf is loaded into your vehicle or delivered curbside, <strong>it will not be removed, exchanged, or
          picked back up</strong> under any circumstances. All sales are final and this paragraph constitutes Affordable Landscape’s official
          <strong>no‑return policy</strong> for recycled turf.
        </p>

        <h3>10.7.6 Customer Responsibility After Loading or Delivery</h3>
        <p>
          Once turf is loaded or delivered curbside, the customer assumes full responsibility for all transport, securing of loads,
          driving conditions, unloading, cutting straps, roll handling, and safety precautions. Affordable Landscape is not responsible for injuries,
          accidents, damage, or losses occurring after curbside delivery or pickup, including those arising from improper tie‑downs,
          shifting loads, or attempts to unload without proper equipment or assistance.
        </p>
        <h3>10.7.7 Recycled Turf Health &amp; Chemical Exposure Warning (California Proposition 65)</h3>
        <p>
          Recycled synthetic turf, backing, infill materials (including but not limited to crumb rubber, sand, plastics, adhesives, and
          coatings), and related components may contain chemicals known to the State of California to cause cancer, birth defects, or
          other reproductive harm. These chemicals can include, but are not limited to, heavy metals, polycyclic aromatic hydrocarbons
          (PAHs), plasticizers, and other compounds associated with recycled rubber and synthetic materials.
        </p>
        <p>
          By purchasing or using recycled turf from Affordable Landscape or its affiliated turf yards, you acknowledge and accept that:
          (a) the product is used material and may contain such chemicals; (b) you are responsible for evaluating whether the product
          is appropriate for your intended use; and (c) you have been provided with this <strong>Proposition 65 health warning</strong>. For more
          information about potential exposures and health risks, you may visit the official California Proposition 65 website at
          www.P65Warnings.ca.gov.
        </p>
        <h3>10.8 One‑Year Labor Warranty (California‑Required Warranty)</h3>
        <p>
          Affordable Landscape provides a <strong>minimum one‑year warranty on all labor</strong> performed on residential construction
          and landscape installations, as required under California law. This warranty covers workmanship defects arising from the
          installation, construction, or labor portion of your project. Materials, manufactured products, turf rolls, irrigation
          components, lighting, and third‑party items are covered only by their respective manufacturer warranties unless otherwise
          stated in writing.
        </p>
        <p>
          This labor warranty does not apply to: (a) damage caused by misuse, pets, flooding, improper maintenance, wear and tear,
          weather events, or acts of God; (b) recycled turf, which is sold strictly as‑is and carries no warranty; or (c) alterations,
          modifications, or repairs performed by anyone other than Graystone or its authorized representatives. Warranty claims must be
          submitted in writing within the warranty period and include photos or videos showing the issue.
        </p>

        <h3>10.9 Mechanic’s Liens — Mandatory California Homeowner Notice</h3>
        <p>
          Under California law, any contractor, subcontractor, laborer, material supplier, or equipment provider who contributes
          labor, skill, materials, or services to your project has the legal right to record a <strong>Mechanic’s Lien</strong> against your
          property if they are not paid for their contribution—even if you have already paid Graystone in full. This means that
          individuals or companies who are not directly contracted with you may still enforce lien rights if they were involved in
          the improvement of your property.
        </p>
        <p>
          A Mechanic’s Lien is a legal claim against the property itself and may result in foreclosure if left unresolved. California
          law requires that we notify you of these rights so that you understand the importance of ensuring that all workers and
          suppliers are paid appropriately through the project’s payment schedule.
        </p>
        <p>
          Before a lien can be recorded, most subcontractors and suppliers must first provide you with a document called a
          <strong>Preliminary Notice</strong>. This notice is <em>not</em> a lien—it is simply a legal requirement that preserves their right to file
          a lien later if unpaid. Receiving a Preliminary Notice does not indicate a problem; it is standard practice on construction
          projects across California.
        </p>
        <p>
          To protect yourself from mechanic’s liens, California law allows homeowners to request and use:
        </p>
        <ul>
          <li><strong>Conditional Lien Releases</strong> — provided when payment is promised but not yet cleared.</li>
          <li><strong>Unconditional Lien Releases</strong> — provided when payment has fully cleared and no further lien rights remain.</li>
          <li><strong>Joint checks</strong> — allowing payment to both Graystone and a subcontractor or supplier simultaneously.</li>
          <li><strong>Payment tracking</strong> — requesting confirmation that subcontractors and suppliers have been paid.</li>
        </ul>
        <p>
          Affordable Landscape follows all California requirements for providing lien releases corresponding to each progress
          payment or final payment. Homeowners may request copies of subcontractor or supplier lien releases at any time. We encourage
          all customers to review lien notices carefully and retain them for their records.
        </p>
        <p>
          You acknowledge that by entering into a construction contract with Affordable Landscape, you understand your rights and
          responsibilities relating to Mechanic’s Liens as required under California Civil Code §§ 8000–9566.
        </p>

        <h3>10.10 PACE Financing &amp; Property Tax Assessments</h3>
        <p>
          Certain qualifying improvements—such as artificial grass, irrigation upgrades, and other energy‑ or water‑efficient
          products—may be eligible for financing through Property Assessed Clean Energy (<strong>PACE</strong>) or similar property tax
          assessment programs. Participation in PACE programs is optional and subject to approval by the PACE provider and applicable
          program rules established by local or state authorities.
        </p>
        <p>
          By choosing to use a PACE program, you understand and agree that: (a) the financing is typically secured by a lien or special
          assessment recorded against your property; (b) repayment is generally made through your property tax bill over a number of
          years; (c) the assessment may run with the land and may be transferable to subsequent owners, subject to lender or buyer
          requirements; and (d) the existence of a PACE assessment may affect your ability to refinance or sell your property,
          depending on the policies of your current or future mortgage lender and the requirements of potential buyers.
        </p>
        <p>
          Graystone is not a lender, PACE administrator, financial institution, or tax advisor. We do not underwrite, approve, or
          service PACE financing. You are solely responsible for reviewing all PACE program documents, disclosures, and legal or
          financial implications directly with the PACE provider and, where appropriate, with your own legal, tax, or financial
          advisors before enrolling in any PACE or similar property tax assessment program.
        </p>
      </section>

      <section>
        <h2>11. Severability & Entire Agreement</h2>
        <p>
          If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force
          and effect. These Terms, together with our Privacy Policy and any additional written agreements or work orders you sign with
          Affordable Landscape, constitute the entire agreement between you and Affordable Landscape regarding your use of our sites and related services.
        </p>
      </section>

      <section>
        <h2>12. Updates to Terms</h2>
        <p>
          We may revise these Terms from time to time to reflect changes in our services, legal requirements, or operational practices.
          Updated versions will be posted on our website with a new effective date. Your continued use of our sites or services after
          such changes become effective constitutes your acceptance of the revised Terms.
        </p>
      </section>

      <section>
        <h2>13. Contact Information</h2>
        <p>
          Affordable Landscape — Licensed C-27 Landscape Contractor (CSLB #940083)<br/>
          Office: 519 N 41st St, San Diego, CA 92102<br/>
          Phone: <a href="tel:+16195877052">+1 (619) 587‑7052</a><br/>
          Email: <a href="mailto:hachireyes@yahoo.com">hachireyes@yahoo.com</a>
        </p>
      </section>

      <section>
        <h2>14. Acceptance of Terms</h2>
        <p>
          By using our websites, booking an appointment, submitting a quote or project request, uploading project documentation, or
          receiving services from Affordable Landscape, you acknowledge that you have read, understood, and agree to be bound by these
          Terms and Conditions.
        </p>
      </section>
    </div>
  );
}

export default TermsAndConditions;